/**
 * @file Team-scoped type option visibility helpers.
 */

import { state, appData } from '../../app/state/state.js';

function getCurrentScopedTypeUser() {
    const currentUserId = state?.currentUser?.uid;
    if (currentUserId) {
        const liveUser = (appData?.users || []).find((user) => user.id === currentUserId);
        if (liveUser) {return liveUser;}
    }

    return state?.currentUserProfile || null;
}

/**
 * Checks whether a user can access a team-scoped type option.
 * @param {Array<string>} teamIds Type option team IDs.
 * @param {object | null} [user=getCurrentScopedTypeUser()] User record.
 * @returns {boolean} True when unscoped or user is in an allowed team.
 */
export function userHasTeamTypeAccess(teamIds, user = getCurrentScopedTypeUser()) {
    if (!Array.isArray(teamIds) || teamIds.length === 0) {return true;}

    const currentUserId = user?.id || user?.uid || state?.currentUser?.uid || null;
    const userTeamIds = (appData?.teams || [])
        .filter((team) => currentUserId && Array.isArray(team?.members) && team.members.includes(currentUserId))
        .map((team) => team.id);

    return teamIds.some((teamId) => userTeamIds.includes(teamId));
}

/**
 * Returns type options visible to a user while preserving selected values.
 * @param {Array<object>} typeOptions Configured type options.
 * @param {Array<string>} selectedIds Already selected type IDs.
 * @param {object | null} [user=getCurrentScopedTypeUser()] User record.
 * @returns {Array<object>} Visible/addable type options.
 */
export function getScopedTypeOptions(
    typeOptions = [],
    selectedIds = [],
    user = getCurrentScopedTypeUser(),
) {
    const options = Array.isArray(typeOptions) ? typeOptions : [];
    const selectedIdSet = new Set(
        (Array.isArray(selectedIds) ? selectedIds : []).filter(Boolean),
    );
    const mergedOptions = [];

    options.forEach((option) => {
        if (!option?.id) {return;}
        const isAddable = (
            option.isActive !== false &&
            userHasTeamTypeAccess(option.teamIds, user)
        );

        if (selectedIdSet.has(option.id)) {
            mergedOptions.push({ ...option, isAddable });
            return;
        }

        if (isAddable) {
            mergedOptions.push({ ...option, isAddable: true });
        }
    });

    return mergedOptions.sort((a, b) => {
        const orderA = Number.isFinite(Number(a?.order)) ? Number(a.order) : 999;
        const orderB = Number.isFinite(Number(b?.order)) ? Number(b.order) : 999;
        if (orderA !== orderB) {return orderA - orderB;}
        return (a?.name || "").localeCompare(b?.name || "");
    });
}
